// Unleashed CSV importer wizard (BUILD_SPEC.md §5.9, §2.13; plan.md G16/G26):
// paste or upload a CSV, pick a kind + optional column mapping, VALIDATE (dry
// run — never writes to products/suppliers/customers/stock_*) and review the
// per-row report, then COMMIT as a deliberate second step. The two-step
// validate→commit split is prominent by construction: commit is only offered
// once a job's status is VALIDATED, so there is no accidental commit path.
import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  FileInput,
  Group,
  Paper,
  Select,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconFileImport, IconUpload } from '@tabler/icons-react';
import { IMPORT_JOB_KINDS, type ImportJobKind } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { DocStatusBadge } from '../../../components/DocStatusBadge';
import { useIsAdmin } from '../permissions';
import { useCommitImportJob, useCreateImportJob, useCustomers, useLocations, useValidateImportJob } from '../api';
import type { ImportJobDto } from '../types';
import { IMPORT_TARGET_FIELDS, sniffCsvHeaders } from './csvPreview';

// Friendlier label than the raw enum value for the one kind that isn't a
// literal "master data" import — every other kind's label is just its enum
// value (unchanged behaviour).
const KIND_LABELS: Partial<Record<ImportJobKind, string>> = {
  FBA_SALES: 'FBA (Fulfilled by Amazon) sales',
};

export function ImportJobWizard() {
  const isAdmin = useIsAdmin();
  const [kind, setKind] = useState<ImportJobKind>('PRODUCTS');
  const [inputMode, setInputMode] = useState<'paste' | 'upload'>('paste');
  const [csvContent, setCsvContent] = useState('');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [job, setJob] = useState<ImportJobDto | null>(null);

  // FBA_SALES-only job params (goal: "customer picker, from-location picker,
  // period label ... Do NOT hardcode any location/customer") — a picker, not a
  // CSV column, since an FBA settlement CSV carries no customer/location/
  // period of its own (see @zebra/core's fbaSalesJobParamsSchema doc comment).
  const [fbaCustomerId, setFbaCustomerId] = useState('');
  const [fbaFromLocationId, setFbaFromLocationId] = useState('');
  const [fbaPeriodLabel, setFbaPeriodLabel] = useState('');

  const create = useCreateImportJob();
  const validate = useValidateImportJob(job?.id ?? '');
  const commit = useCommitImportJob(job?.id ?? '');
  // Both pickers are FBA_SALES-only in this wizard (the only place either
  // query's result is used, see customerOptions/locationOptions below) — gate
  // them so every other kind doesn't fire two unused list fetches on load.
  const customersQuery = useCustomers({ isActive: true, enabled: kind === 'FBA_SALES' });
  const locationsQuery = useLocations({ enabled: kind === 'FBA_SALES' });

  const headers = useMemo(() => sniffCsvHeaders(csvContent), [csvContent]);
  const targetFields = IMPORT_TARGET_FIELDS[kind] ?? [];
  const customerOptions = useMemo(
    () => (customersQuery.data?.items ?? []).map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` })),
    [customersQuery.data],
  );
  const locationOptions = useMemo(
    () =>
      (locationsQuery.data?.items ?? [])
        .filter((l) => l.isActive)
        .map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })),
    [locationsQuery.data],
  );
  const fbaParamsComplete = fbaCustomerId.trim() !== '' && fbaFromLocationId.trim() !== '' && fbaPeriodLabel.trim() !== '';

  function handleFile(file: File | null) {
    if (!file) return;
    file.text().then(setCsvContent).catch(() => undefined);
  }

  function updateMapping(header: string, target: string) {
    setMapping((prev) => {
      const next = { ...prev };
      if (target) next[header] = target;
      else delete next[header];
      return next;
    });
  }

  function handleCreate() {
    const params =
      kind === 'FBA_SALES'
        ? { customerId: fbaCustomerId, fromLocationId: fbaFromLocationId, periodLabel: fbaPeriodLabel.trim() }
        : undefined;
    create.mutate(
      { kind, csvContent, mapping: Object.keys(mapping).length > 0 ? mapping : undefined, params },
      { onSuccess: setJob },
    );
  }

  function handleValidate() {
    validate.mutate(undefined, { onSuccess: setJob });
  }

  function handleCommit() {
    commit.mutate(undefined, { onSuccess: setJob });
  }

  function startOver() {
    setJob(null);
    setCsvContent('');
    setMapping({});
    setFbaCustomerId('');
    setFbaFromLocationId('');
    setFbaPeriodLabel('');
    create.reset();
    validate.reset();
    commit.reset();
  }

  if (!isAdmin) {
    return (
      <Paper withBorder radius="md" p="lg">
        <Text size="sm" c="dimmed">
          ADMIN role is required to run an import — this is a bulk data operation.
        </Text>
      </Paper>
    );
  }

  // -------------------------------------------------------------------
  // Step 2/3: an import job exists — show validate/commit + the report.
  // -------------------------------------------------------------------
  if (job) {
    const report = job.report;
    return (
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <Title order={3}>Import job — {job.kind}</Title>
            <DocStatusBadge status={job.status} domain="importJob" />
          </Group>
          <Button variant="default" size="xs" onClick={startOver}>
            Start new import
          </Button>
        </Group>

        {report && (
          <Group gap="xl">
            <Stat label="Total rows" value={String(report.totalRows)} />
            <Stat label="OK" value={String(report.okCount)} color="green" />
            <Stat label="Errors" value={String(report.errorCount)} color={report.errorCount > 0 ? 'red' : undefined} />
            {report.committedCount !== undefined && <Stat label="Committed" value={String(report.committedCount)} color="blue" />}
          </Group>
        )}

        {validate.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {validate.error instanceof ApiRequestError ? validate.error.message : 'Validation failed.'}
          </Alert>
        )}
        {commit.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {commit.error instanceof ApiRequestError ? commit.error.message : 'Commit failed.'}
          </Alert>
        )}
        {job.status === 'COMMITTED' && (
          <Alert color="green" icon={<IconCircleCheck size={16} />}>
            {job.kind === 'FBA_SALES'
              ? `Committed ${report?.committedCount ?? 0} row(s) into one posted dispatch. Raise the invoice from Sales -> Raise invoices when you're ready — Amazon's fees are not booked here and stay in Xero.`
              : `Committed ${report?.committedCount ?? 0} row(s).`}
          </Alert>
        )}
        {job.status === 'FAILED' && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Fix and re-import">
            This CSV has row errors — fix the source data or column mapping and start a new import (jobs are immutable
            once created).
          </Alert>
        )}

        {report && report.rows.length > 0 && (
          <Paper withBorder radius="md">
            <Table.ScrollContainer minWidth={480} mah={420}>
              <Table striped verticalSpacing="xs" stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Row</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Detail</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {report.rows.map((r) => (
                    <Table.Tr key={r.row}>
                      <Table.Td>{r.row}</Table.Td>
                      <Table.Td>
                        <Badge size="xs" color={r.ok ? 'green' : 'red'} variant="light">
                          {r.ok ? 'OK' : 'ERROR'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c={r.ok ? 'dimmed' : 'red'}>
                          {r.ok ? JSON.stringify(r.data) : (r.errors ?? []).join('; ')}
                        </Text>
                        {r.ok && r.notes && r.notes.length > 0 && (
                          <Text size="xs" c="orange" mt={2}>
                            Interpreted: {r.notes.join('; ')}
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>
        )}

        <Group>
          {job.status === 'DRAFT' && (
            <Button onClick={handleValidate} loading={validate.isPending}>
              Validate
            </Button>
          )}
          {job.status === 'FAILED' && (
            <Button onClick={handleValidate} loading={validate.isPending} variant="default">
              Re-run validation
            </Button>
          )}
          {job.status === 'VALIDATED' && (
            <Tooltip
              label={
                job.kind === 'FBA_SALES'
                  ? 'Aggregates every SKU in this file into ONE posted dispatch — this cannot be undone; start a new import to fix mistakes.'
                  : 'Writes every OK row into products/suppliers/customers/stock — this cannot be undone; start a new import to fix mistakes.'
              }
            >
              <Button onClick={handleCommit} loading={commit.isPending} color="brandGreen">
                Commit
              </Button>
            </Tooltip>
          )}
        </Group>
      </Stack>
    );
  }

  // -------------------------------------------------------------------
  // Step 1: pick kind + CSV content + optional column mapping.
  // -------------------------------------------------------------------
  return (
    <Stack gap="md" maw={720}>
      <Title order={3}>New import</Title>
      <Text size="sm" c="dimmed">
        Bulk-import data from a CSV — validate it first, then commit as a separate, deliberate step.
      </Text>

      <Select
        label="Import kind"
        description="Which master data this CSV creates or updates (FBA sales instead books a dispatch — see below)."
        data={IMPORT_JOB_KINDS.map((k) => ({ value: k, label: KIND_LABELS[k] ?? k }))}
        value={kind}
        onChange={(v) => v && setKind(v as ImportJobKind)}
      />

      {kind === 'FBA_SALES' && (
        <Paper withBorder radius="md" p="md">
          <Stack gap="sm">
            <Text size="sm" fw={500}>
              FBA sale parameters
            </Text>
            <Text size="xs" c="dimmed">
              These apply to the WHOLE file — an Amazon settlement CSV carries no customer, location or period of its
              own, so they're picked once here rather than per row.
            </Text>
            <Select
              label="Customer"
              description="Who this period's Amazon sales are invoiced to. Set this customer's invoicing mode to CONSOLIDATED so the whole period rolls into one invoice."
              searchable
              data={customerOptions}
              value={fbaCustomerId || null}
              onChange={(v) => setFbaCustomerId(v ?? '')}
              placeholder="Choose a customer…"
              nothingFoundMessage={customersQuery.isLoading ? 'Loading…' : 'No customers found'}
              comboboxProps={{ withinPortal: true }}
            />
            <Select
              label="From location"
              description="Stock location the FBA units are dispatched from."
              searchable
              data={locationOptions}
              value={fbaFromLocationId || null}
              onChange={(v) => setFbaFromLocationId(v ?? '')}
              placeholder="Choose a location…"
              nothingFoundMessage={locationsQuery.isLoading ? 'Loading…' : 'No locations found'}
              comboboxProps={{ withinPortal: true }}
            />
            <TextInput
              label="Period label"
              description="Free text, e.g. '2026-07' — becomes the created dispatch's reference note."
              value={fbaPeriodLabel}
              onChange={(e) => setFbaPeriodLabel(e.currentTarget.value)}
              placeholder="e.g. 2026-07"
            />
          </Stack>
        </Paper>
      )}

      <div>
        <Text size="sm" fw={500} mb={4}>
          CSV source
        </Text>
        <SegmentedControl
          value={inputMode}
          onChange={(v) => setInputMode(v as 'paste' | 'upload')}
          data={[
            { value: 'paste', label: 'Paste' },
            { value: 'upload', label: 'Upload file' },
          ]}
        />
      </div>

      {inputMode === 'paste' ? (
        <Textarea
          label="CSV content"
          placeholder="sku,name,type,baseUom,..."
          autosize
          minRows={6}
          maxRows={14}
          value={csvContent}
          onChange={(e) => setCsvContent(e.currentTarget.value)}
        />
      ) : (
        <FileInput label="CSV file" placeholder="Choose a .csv file" accept=".csv,text/csv" leftSection={<IconUpload size={16} />} onChange={handleFile} clearable />
      )}

      {headers.length > 0 && (
        <Paper withBorder radius="md" p="md">
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Column mapping (optional — leave blank to use the CSV header as the field name)
            </Text>
            <Table.ScrollContainer minWidth={640}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>CSV column</Table.Th>
                    <Table.Th>Maps to</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {headers.map((header) => (
                    <Table.Tr key={header}>
                      <Table.Td>{header}</Table.Td>
                      <Table.Td>
                        <Select
                          placeholder={targetFields.includes(header) ? header : 'Same as CSV column'}
                          data={targetFields}
                          clearable
                          value={mapping[header] ?? null}
                          onChange={(v) => updateMapping(header, v ?? '')}
                          size="xs"
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>
        </Paper>
      )}

      {create.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {create.error instanceof ApiRequestError ? create.error.message : 'Failed to create import job.'}
        </Alert>
      )}

      <Group>
        <Button
          leftSection={<IconFileImport size={16} />}
          onClick={handleCreate}
          loading={create.isPending}
          disabled={!csvContent.trim() || (kind === 'FBA_SALES' && !fbaParamsComplete)}
        >
          Create import job
        </Button>
      </Group>
    </Stack>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fw={700} c={color}>
        {value}
      </Text>
    </div>
  );
}
